import { useMutation } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { RetailItem, RetailItemSearchCriteria } from "src/dm/interfaces/RetailItem";

interface SearchEnvelope {
  items: RetailItem[];
  total: number;
}

// Sibling to /dm/V1/deals — the retail-items endpoint backs the Add-Item
// search dialog. We deliberately do NOT key it under a deal id; the catalog
// is global.
const SEARCH_URL = "/dm/V1/retail-items/search";

/**
 * Fire a one-shot search against RP_ITEM_AUTH_BANNERS_MV. Mutation rather
 * than useQuery so the search only runs when the user clicks Search (we
 * don't want a query firing on every keystroke).
 */
export const useRetailItemSearch = () =>
  useMutation({
    mutationFn: async (criteria: RetailItemSearchCriteria): Promise<RetailItem[]> => {
      const params: Record<string, string> = {};
      const add = (k: string, v: unknown) => {
        if (v === null || v === undefined) return;
        if (typeof v === "string" && v.trim() === "") return;
        params[k] = String(v);
      };
      add("retailItemNumber", criteria.retailItemNumber);
      add("upc", criteria.upc);
      add("caseUpc", criteria.caseUpc);
      add("whitemNumber", criteria.whitemNumber);
      add("description", criteria.description);
      add("pack", criteria.pack);
      add("size", criteria.size);
      add("vendorNumber", criteria.vendorNumber);
      add("familyCode", criteria.familyCode);
      add("groupCode", criteria.groupCode);
      add("subgroupCode", criteria.subgroupCode);
      add("priceGroupCode", criteria.priceGroupCode);
      add("itemStatus", criteria.itemStatus);
      add("pageSize", criteria.pageSize ?? 100);

      const response = await apiClient.get<SearchEnvelope>(SEARCH_URL, { params });
      return response.data.items ?? [];
    },
  });

/** One-off retail-item lookup by RITEM_NO — used by the Item Summary tab. */
export const fetchRetailItemByNumber = async (
  retailItemNumber: number
): Promise<RetailItem | null> => {
  try {
    const response = await apiClient.get<RetailItem>(`/dm/V1/retail-items/${retailItemNumber}`);
    return response.data;
  } catch {
    return null;
  }
};
